import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  ReactElement,
} from 'react';
import {
  Alert,
  Button,
  Modal,
  Paragraph,
  Spinner,
  CheckCircleIcon,
  ErrorOutlineIcon,
} from 'elmo-elements';
import {
  useGetAiGenerationStatusQuery,
  useRetryAiGenerationMutation,
} from '@/modules/services';
import type { SubmitFileResult } from '@/modules/services/aiCreate';
import AiPoweredBadge from '@/components/AiPoweredBadge';
import {
  AI_BUSY_MESSAGE,
  AI_QUOTA_EXHAUSTED_MESSAGE,
} from './useAiCreateSubmit';

const STEPS = [
  'Reading context',
  'Structuring content',
  'Generating slides',
] as const;

type StepState = 'completed' | 'active' | 'failed' | 'pending';

export const GENERATION_FAILED_MESSAGE = 'Something went wrong on our side';
// A failure another attempt cannot fix is not ours, and saying it is points the admin at
// Try again instead of at the thing they have to change.
export const GENERATION_UNRETRYABLE_MESSAGE =
  'This module cannot be created as it is';
/** Flash copy for the paths that close the modal instead of rendering the failure. */
export const GENERATION_ABANDONED_MESSAGE =
  'We could not finish creating this module. Nothing was saved to My Library.';
export const GENERATION_FAILED_DETAIL =
  'Your prompt was fine. Nothing was saved to My Library. Try again, or come back in a few minutes.';
export const RETRY_FAILED_MESSAGE =
  "We couldn't restart this module. Please try again.";
export const RETRY_MOVED_ON_MESSAGE =
  'This module is no longer waiting to be retried. Go back to setup to start again.';

/**
 * A retry is admitted like a fresh start, so it fails in the same ways. 409 means the
 * request has moved on, and no further attempt on it can work.
 */
const toRetryError = (err: any): { message: string; movedOn: boolean } => {
  if (err?.status === 409) {
    return { message: RETRY_MOVED_ON_MESSAGE, movedOn: true };
  }
  if (err?.data?.error === 'MONTHLY_CAP_EXCEEDED') {
    return { message: AI_QUOTA_EXHAUSTED_MESSAGE, movedOn: false };
  }
  if (err?.data?.error === 'AI_CONCURRENCY_LIMIT') {
    return { message: AI_BUSY_MESSAGE, movedOn: false };
  }
  return { message: RETRY_FAILED_MESSAGE, movedOn: false };
};

/** A verdict the admin has to act on: anything but a pass or an overridden relevance flag. */
const isFailingVerdict = ({ status, accepted }: SubmitFileResult): boolean =>
  status !== 'ok' && !(status === 'not_relevant' && accepted);

export interface AiGeneratingModalProps {
  isOpened: boolean;
  /** Null until the start request resolves; polling is skipped while null. */
  requestId: string | null;
  name: string;
  onDone: (elearningId: string) => void;
  onError: (message: string) => void;
  /** Failure only: returns to the setup form instead of closing the flow. */
  onBackToSetup: () => void;
  /**
   * The worker sent the request back to its draft over a file only it could judge
   * (LMS2-5141): back to the form, with each file's verdict to show on its field.
   */
  onFileVerdict: (files: SubmitFileResult[]) => void;
}

/**
 * Step-by-step progress screen for Create with AI (LMS2-5101). Mirrors
 * PptConvertingModal's derived-step pattern, with one deliberate difference:
 * there is NO close control and NO run-in-background option — the admin stays on
 * this screen until generation completes or fails (no background run for MVP).
 * The Modal's built-in header X still renders without a closeModal prop (it just
 * no-ops), so index.scss hides it for this modal id; the default footer Cancel is
 * suppressed via hasCloseFooterButton={false}.
 */
const AiGeneratingModal = ({
  isOpened,
  requestId,
  name,
  onDone,
  onError,
  onBackToSetup,
  onFileVerdict,
}: AiGeneratingModalProps): ReactElement => {
  const {
    data,
    error: pollError,
    fulfilledTimeStamp,
    refetch,
  } = useGetAiGenerationStatusQuery(requestId ?? '', {
    skip: !isOpened || !requestId,
    pollingInterval: 5000,
    refetchOnMountOrArgChange: true,
  });
  // The status cache is keyed on the request id, and a request the worker sent back to
  // its draft keeps that id for the next click (LMS2-5141). Until this modal's own first
  // poll lands, the cache can still hold the previous attempt's answer: the 'draft' that
  // closed the last modal would send the admin straight back to the form, with that
  // attempt's file verdict hung on the file that replaced it. Refetching on mount does not
  // clear it, so anything answered before this modal opened is treated as no answer yet.
  const openedAt = useRef(Date.now());
  const status =
    fulfilledTimeStamp !== undefined && fulfilledTimeStamp >= openedAt.current
      ? data
      : undefined;
  const [retryAiGeneration, { isLoading: isRetrying }] =
    useRetryAiGenerationMutation();
  const [retryError, setRetryError] = useState<string | null>(null);
  // Set when the server says this request can never be retried, so the button goes away
  // instead of offering a click that will 409 again.
  const [retryMovedOn, setRetryMovedOn] = useState(false);
  // True from the moment a retry is accepted until the first poll that answers after it.
  // The poll can still be holding the old failure for a few seconds, and the admin should
  // see the new run start, not the old error.
  const [isRestarting, setIsRestarting] = useState(false);
  const retriedAt = useRef(0);

  const generationStatus = status?.status;
  const slidesDone = status?.slidesDone ?? 0;
  const slidesPlanned = status?.slidesPlanned ?? 0;
  const progress =
    slidesPlanned > 0 ? Math.min((slidesDone / slidesPlanned) * 100, 100) : 0;

  const isCompleted = generationStatus === 'completed';
  const isFailed = generationStatus === 'failed' && !isRestarting;
  // Only a technical failure can be retried. A blocked prompt or a stale file verdict
  // fails the same way on every attempt, so the admin is sent back to the form instead.
  const isRetryable =
    !retryMovedOn && (!status?.errorCode || status.errorCode === 'INTERNAL');
  let activeStep: number;
  if (isCompleted) {
    activeStep = STEPS.length;
  } else if (slidesDone > 0) {
    // Slides are being produced; stays active until the request completes.
    activeStep = 2;
  } else if (generationStatus === 'generating') {
    activeStep = 1;
  } else {
    // 'queued', or the start request / first poll hasn't landed yet.
    activeStep = 0;
  }

  const stepState = (index: number): StepState => {
    if (isCompleted) return 'completed';
    if (isFailed && index === activeStep) return 'failed';
    if (index < activeStep) return 'completed';
    if (index === activeStep) return 'active';
    return 'pending';
  };

  const stepIcon = (state: StepState): ReactElement => {
    if (state === 'completed') return <CheckCircleIcon />;
    if (state === 'failed') return <ErrorOutlineIcon />;
    if (state === 'active') return <Spinner size="s" />;
    return <span className="converting-steps__dot" aria-hidden="true" />;
  };

  // Fired-once guard (ImportStatusBadge pattern): polling keeps delivering the
  // terminal status, so without it onDone/onError would re-fire every render.
  const settledRef = useRef(false);
  const pollFailuresRef = useRef(0);
  useEffect(() => {
    if (settledRef.current || !status) return;
    if (status.status === 'completed') {
      settledRef.current = true;
      if (status.elearningId) {
        onDone(status.elearningId);
      } else {
        // Contract violation (completed must carry the id) — fail rather than
        // navigating to a null module.
        onError(GENERATION_ABANDONED_MESSAGE);
      }
    }
    // Not a failure of the run: a file needs changing. Straight back to the form, where
    // the file's own field says why — the same state a submit-time verdict leaves. A
    // draft that names no failing file would come back with the note and nothing flagged,
    // and Create would send the same draft round again, so it ends like any other answer
    // this modal cannot act on. Nothing will run from a draft, so it never keeps polling.
    if (status.status === 'draft') {
      settledRef.current = true;
      const files = status.files ?? [];
      if (files.some(isFailingVerdict)) {
        onFileVerdict(files);
      } else {
        onError(GENERATION_ABANDONED_MESSAGE);
      }
    }
    // A technical failure deliberately does NOT settle or leave: it is rendered here
    // with a retry, which is the whole point of LMS2-5103. Only the admin closing the
    // modal ends the attempt.
  }, [status, onDone, onError, onFileVerdict]);

  // Over once a poll answers after the retry, whatever it says. Waiting for a non-failed
  // status instead would strand the admin in a closeless modal when the new run fails
  // before the first poll catches it.
  useEffect(() => {
    if (
      isRestarting &&
      fulfilledTimeStamp &&
      fulfilledTimeStamp > retriedAt.current
    ) {
      setIsRestarting(false);
    }
  }, [isRestarting, fulfilledTimeStamp]);

  // A run that is moving again clears what the last refusal said. Without this a 409 from
  // a stale double-click would hide Try again for the rest of the modal, including when
  // the run it collided with later fails for a reason a retry could fix.
  useEffect(() => {
    if (status && status.status !== 'failed') {
      setRetryMovedOn(false);
      setRetryError(null);
    }
  }, [status]);

  const handleRetry = useCallback(async () => {
    if (!requestId) return;
    setRetryError(null);
    setIsRestarting(true);
    // Nothing may end the restart while the POST is in flight: the row is still failed
    // on the server, so a poll landing now would flash the old failure back.
    retriedAt.current = Number.POSITIVE_INFINITY;
    try {
      await retryAiGeneration(requestId).unwrap();
      // Now only a poll that could have seen the queued row ends it.
      retriedAt.current = Date.now();
      // A fresh poll rather than waiting out the 5s interval, so the steps restart
      // as soon as the admin has clicked.
      pollFailuresRef.current = 0;
      refetch();
    } catch (err) {
      const { message, movedOn } = toRetryError(err);
      setIsRestarting(false);
      setRetryError(message);
      setRetryMovedOn(movedOn);
    }
  }, [requestId, retryAiGeneration, refetch]);

  // The base query only intercepts 401s, so a failing poll (outage, 5xx) would
  // otherwise spin this closeless modal forever. Three consecutive failures
  // (~15s) tolerates replica lag on the first poll and transient blips, then
  // bails out through the same flash-and-close path as a failed generation.
  // Keyed on `error` only: RTK clears it on every fulfilled poll, whereas
  // structural sharing keeps `data` reference-equal while the queued payload is
  // unchanged — a data-keyed reset would never fire after the first success and
  // non-consecutive blips would falsely trip the bailout.
  useEffect(() => {
    if (!pollError) {
      pollFailuresRef.current = 0;
      return;
    }
    if (settledRef.current) return;
    pollFailuresRef.current += 1;
    if (pollFailuresRef.current >= 3) {
      settledRef.current = true;
      onError(GENERATION_ABANDONED_MESSAGE);
    }
  }, [pollError, onError]);

  return (
    <Modal
      id="AiGeneratingModal"
      className={
        isFailed ? 'new-module ai-generating-modal--failed' : 'new-module'
      }
      type="large"
      isOpened={isOpened}
      title={
        isFailed
          ? "We couldn't finish this module"
          : 'Generating your module...'
      }
      hasCloseFooterButton={false}
      // The failure state is the only way out of this screen, so it is the only state
      // with controls. While it is generating there is still no close and no
      // run-in-background (LMS2-5101).
      {...(isFailed
        ? {
            // The footer close button is the wireframe's "Back to setup"; the header
            // X shares its handler, so neither exit throws away what was typed.
            closeModal: onBackToSetup,
            closeLabel: 'Back to setup',
            hasCloseFooterButton: true,
            ...(isRetryable
              ? {
                  primaryButton: (
                    <Button
                      type="primary"
                      isUppercase={false}
                      isDisabled={isRetrying}
                      isLoading={isRetrying}
                      onClick={handleRetry}
                    >
                      Try again
                    </Button>
                  ),
                }
              : {}),
          }
        : {})}
      ariaLabel={
        isFailed
          ? `We couldn't finish this module. ${
              isRetryable ? 'Try again, or go' : 'Go'
            } back to setup.`
          : 'Generating your module. This dialog cannot be dismissed and closes automatically when generation finishes.'
      }
    >
      <div className="converting">
        <AiPoweredBadge className="converting__badge" />

        <Paragraph isBold className="converting__filename">
          {name}
        </Paragraph>
        {!isFailed && (
          <Paragraph size="sm" color="gray">
            This will take a few minutes
          </Paragraph>
        )}

        {retryError && (
          <Alert
            type="danger"
            className="converting__error"
            message={retryError}
          />
        )}

        <ol
          className="converting-steps"
          aria-label="Generation steps"
          aria-live="polite"
        >
          {STEPS.map((label, index) => {
            const state = stepState(index);
            return (
              <li
                key={label}
                className={`converting-steps__item converting-steps__item--${state}`}
              >
                <span className="converting-steps__icon">
                  {stepIcon(state)}
                </span>
                <span className="converting-steps__label">{label}</span>
              </li>
            );
          })}
        </ol>

        {slidesPlanned > 0 && (
          <div className="converting-progress">
            <div
              className="converting-progress__bar"
              role="progressbar"
              aria-label="Generation progress"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(progress)}
            >
              <div
                className="converting-progress__fill"
                style={{ width: `${progress}%` }}
              />
            </div>
            <Paragraph size="sm" color="gray" align="center">
              <output>
                {slidesDone} of {slidesPlanned} slides created
              </output>
            </Paragraph>
          </div>
        )}
      </div>
      {isFailed && (
        <Alert
          type="danger"
          className="converting__error"
          isCloseable={false}
          heading={
            isRetryable
              ? GENERATION_FAILED_MESSAGE
              : GENERATION_UNRETRYABLE_MESSAGE
          }
          // A retryable failure says the same thing every time, so the wireframe's line
          // reads better than the server's; an unretryable one carries the only detail
          // that tells the admin what to change.
          message={
            isRetryable
              ? GENERATION_FAILED_DETAIL
              : status?.error || GENERATION_FAILED_DETAIL
          }
        />
      )}
    </Modal>
  );
};

export default AiGeneratingModal;
